import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import { useCookies } from 'react-cookie';
import * as yup from 'yup';
import { Formik, Field } from 'formik';

import {
    FovModal,
    FovSelect,
    FovOption,
    FovInput,
    FovButton,
    FovFieldError,
    FovLoader,
    FovTypography,
    VerticalSpacer,
    MESSAGE_TYPES,
} from 'components';
import { getColor } from 'utils';
import { upsertTeamMemberAdmin } from 'store/actions/admin';
import { addMessage } from 'store/actions/app';

const { VITE_API_DEV_HOST } = process.env;

const ButtonContainer = styled('div')`
    display: inline-flex;
    width: 100%;
    max-width: 25rem;
`;

const ToggleLink = styled('span')`
    cursor: pointer;
    color: ${props => getColor(props, 'accent')};
    text-decoration: underline;
`;

const AddToTeamForm = withTheme(props => {
    const { isVisible, onClose, users, onSuccess, theme } = props;

    const dispatch = useDispatch();
    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;
    const selectedTheme = useSelector(state => state.userData.userPreferences.theme);

    const [ teams, setTeams ] = useState([]);
    const [ teamsLoading, setTeamsLoading ] = useState(false);
    const [ createMode, setCreateMode ] = useState(false);

    const fetchTeams = useCallback(async () => {
        const res = await fetch(`${VITE_API_DEV_HOST}/admin/teams?per_page=1000`, {
            headers: { Authorization: 'Bearer ' + token },
        });
        const data = await res.json();
        return data.teams || [];
    }, [ token ]);

    useEffect(() => {
        if (!isVisible) return undefined;

        let cancelled = false;
        const load = async () => {
            setTeamsLoading(true);
            try {
                const result = await fetchTeams();
                if (!cancelled) setTeams(result);
            } catch {
                if (!cancelled) {
                    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
                    dispatch(addMessage([ { message: 'Unable to retrieve teams.', config } ]));
                }
            } finally {
                if (!cancelled) setTeamsLoading(false);
            }
        };
        load();

        return () => { cancelled = true; };
    }, [ isVisible, fetchTeams ]);

    const domain = useMemo(() => {
        const email = users?.[0]?.email || '';
        const at = email.indexOf('@');
        return at === -1 ? '' : email.slice(at);
    }, [ users ]);

    const matchTeam = useCallback(list =>
        list.find(team =>
            team.allowed_domains?.some(allowed => allowed.toLowerCase() === domain.toLowerCase())),
    [ domain ]);

    const defaultTeamId = useMemo(() => {
        const match = matchTeam(teams);
        return match ? String(match.team_id) : '';
    }, [ teams, matchTeam ]);

    useEffect(() => {
        setCreateMode(!defaultTeamId && !!domain);
    }, [ defaultTeamId, domain, isVisible ]);

    const addUsers = useCallback(teamId =>
        Promise.all(users.map(user =>
            dispatch(upsertTeamMemberAdmin(token, teamId, [ { id: user.id, role: 'member' } ]))),
    ), [ users, token, dispatch ]);

    const handleAddToTeam = useCallback((values, setSubmitting) => {
        const { teamId } = values;
        addUsers(teamId)
            .then(() => {
                const team = teams.find(item => String(item.team_id) === String(teamId));
                const name = team?.display_name || 'team';
                const message = `Added ${users.length} user${users.length > 1 ? 's' : ''} to ${name}.`;
                dispatch(addMessage([ { message, config: { type: MESSAGE_TYPES.success, timer: 2.5 } } ]));
                setSubmitting(false);
                onSuccess();
            })
            .catch(() => {
                const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
                dispatch(addMessage([ { message: 'Unable to add users to team.', config } ]));
                setSubmitting(false);
            });
    }, [ addUsers, teams, users, dispatch, onSuccess ]);

    const handleCreateAndAdd = useCallback(async (values, setSubmitting) => {
        const { displayName } = values;
        try {
            const res = await fetch(`${VITE_API_DEV_HOST}/admin/teams`, {
                method: 'POST',
                headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: displayName.replace(' ', '_'),
                    display_name: displayName,
                    allowed_domains: [ domain ],
                }),
            });
            if (!res.ok) throw new Error('create failed');

            const refreshed = await fetchTeams();
            const teamId = matchTeam(refreshed)?.team_id;
            if (!teamId) throw new Error('team not found after create');

            await addUsers(teamId);
            const message = `Created ${displayName} and added ${users.length} user${users.length > 1 ? 's' : ''}.`;
            dispatch(addMessage([ { message, config: { type: MESSAGE_TYPES.success, timer: 2.5 } } ]));
            setSubmitting(false);
            onSuccess();
        } catch {
            const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
            dispatch(addMessage([ { message: 'Unable to create team.', config } ]));
            setSubmitting(false);
        }
    }, [ token, domain, fetchTeams, matchTeam, addUsers, users, dispatch, onSuccess ]);

    const defaults = { teamId: defaultTeamId, displayName: '' };

    const schema = useMemo(() => yup.object().shape(
        createMode
            ? { displayName: yup.string().trim().required('A team name is required.') }
            : { teamId: yup.string().required('Please select a team.') },
    ), [ createMode ]);

    const count = users?.length || 0;
    const summary = `Adding ${count} user${count === 1 ? '' : 's'}${domain ? ` (${domain})` : ''}`;

    return (
        <FovModal
            title='Add to team'
            visible={isVisible}
            onClose={onClose}
        >
            <FovLoader text='Retrieving teams' resource={!teamsLoading}>
                <Formik
                    enableReinitialize={true}
                    initialValues={defaults}
                    validationSchema={schema}
                    onSubmit={(values, handlers) => {
                        const { setSubmitting } = handlers;
                        if (createMode) {
                            handleCreateAndAdd(values, setSubmitting);
                        } else {
                            handleAddToTeam(values, setSubmitting);
                        }
                    }}
                >
                    {form => (
                        <Fragment>
                            <FovTypography>{summary}</FovTypography>
                            <VerticalSpacer size={10} />

                            {!createMode &&
                                <Fragment>
                                    Team
                                    <Field type='select' name='teamId'>
                                        {data => (
                                            <FovSelect
                                                value={data.field.value}
                                                placeholder={teams.length ? 'Select a team' : 'No teams available'}
                                                disabled={teams.length === 0}
                                                onChange={(e, newVal) => {
                                                    form.setFieldValue(data.field.name, newVal);
                                                }}
                                            >
                                                {teams.map(team => (
                                                    <FovOption key={team.team_id} value={String(team.team_id)}>
                                                        {team.display_name}
                                                    </FovOption>
                                                ))}
                                            </FovSelect>
                                        )}
                                    </Field>
                                    <FovFieldError name='teamId' />
                                    {!defaultTeamId && domain &&
                                        <Fragment>
                                            <VerticalSpacer size={10} />
                                            <ToggleLink
                                                theme={theme}
                                                $selectedTheme={selectedTheme}
                                                onClick={() => setCreateMode(true)}
                                            >
                                                {`Create a new team for ${domain}`}
                                            </ToggleLink>
                                        </Fragment>
                                    }
                                </Fragment>
                            }

                            {createMode &&
                                <Fragment>
                                    <FovTypography>{`No team found for ${domain}. Create one:`}</FovTypography>
                                    <VerticalSpacer size={10} />
                                    Team Name
                                    <Field type='text' name='displayName'>
                                        {data => (
                                            <FovInput
                                                value={data.field.value}
                                                {...data.field}
                                                onChange={e => {
                                                    form.setFieldValue(data.field.name, e.target.value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                    <FovFieldError name='displayName' />
                                    <VerticalSpacer size={10} />
                                    <FovTypography>{`Domain: ${domain}`}</FovTypography>
                                    {defaultTeamId === '' && teams.length > 0 &&
                                        <Fragment>
                                            <VerticalSpacer size={10} />
                                            <ToggleLink
                                                theme={theme}
                                                $selectedTheme={selectedTheme}
                                                onClick={() => setCreateMode(false)}
                                            >
                                                Select an existing team instead
                                            </ToggleLink>
                                        </Fragment>
                                    }
                                </Fragment>
                            }

                            <VerticalSpacer size={25} />
                            <ButtonContainer>
                                <FovButton
                                    type='submit'
                                    disabled={form.isSubmitting || (!createMode && teams.length === 0)}
                                    onClick={form.handleSubmit}
                                >
                                    {createMode
                                        ? (form.isSubmitting ? 'Creating...' : 'Create team & add')
                                        : (form.isSubmitting ? 'Adding...' : 'Add to Team')}
                                </FovButton>
                            </ButtonContainer>
                        </Fragment>
                    )}
                </Formik>
            </FovLoader>
        </FovModal>
    );
});

export { AddToTeamForm };
